import { useState, useRef, useCallback } from 'react';
import { apiRequest, API_BASE_URL } from '../config/api';

function generateId() {
  return Math.random().toString(36).substring(2, 15);
}

export default function useChatStream({ onChatCreated, onChatRestricted } = {}) {
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [chatId, setChatId] = useState(null);
  const abortControllerRef = useRef(null);
  const messagesRef = useRef([]);
  const chatIdRef = useRef(null);
  const onChatCreatedRef = useRef(onChatCreated);
  onChatCreatedRef.current = onChatCreated;
  const onChatRestrictedRef = useRef(onChatRestricted);
  onChatRestrictedRef.current = onChatRestricted;

  // Keep refs in sync with state
  const setMessagesAndRef = useCallback((updater) => {
    setMessages(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      messagesRef.current = next;
      return next;
    });
  }, []);

  const setChatIdAndRef = useCallback((id) => {
    setChatId(id);
    chatIdRef.current = id;
  }, []);

  const updateAssistantMessage = useCallback((updater) => {
    setMessagesAndRef(prev => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last && last.role === 'assistant') {
        next[next.length - 1] = updater(last);
      }
      return next;
    });
  }, [setMessagesAndRef]);

  const sendMessage = useCallback(async (text, { model = 'sonnet', thinkingEnabled = false, systemPrompt = null, enabledTools = null, files = [] } = {}) => {
    if (isStreaming) return;

    // Build API messages from current history + new user message
    // Include attachments on user messages so the backend can reconstruct
    // file content blocks for Anthropic on every turn
    const apiMessages = [];
    for (const msg of messagesRef.current) {
      if (msg.role === 'user') {
        const entry = { role: 'user', content: msg.content };
        if (msg.attachments?.length) entry.attachments = msg.attachments;
        apiMessages.push(entry);
      } else if (msg.role === 'assistant' && msg.content) {
        apiMessages.push({ role: 'assistant', content: msg.content });
      }
    }

    // Build attachments metadata for the new message. The backend resolves
    // any non-native text content server-side from the file_id, so the client
    // only ships lightweight metadata.
    const attachments = files.length > 0
      ? files.map(f => ({ file_id: f.file_id, mime_type: f.mime_type, filename: f.filename, size_bytes: f.size_bytes }))
      : undefined;

    const newUserMsg = { role: 'user', content: text };
    if (attachments) newUserMsg.attachments = attachments;
    apiMessages.push(newUserMsg);

    // Track whether this is a new chat (no chatId yet)
    const isNewChat = !chatIdRef.current;

    // Add messages to UI state
    const userMessage = { id: generateId(), role: 'user', content: text, thinking: '', thinkingDone: false, toolUses: [], isStreaming: false, attachments };
    const assistantMessage = { id: generateId(), role: 'assistant', content: '', thinking: '', thinkingDone: false, toolUses: [], isStreaming: true };
    setMessagesAndRef(prev => [...prev, userMessage, assistantMessage]);
    setIsStreaming(true);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          messages: apiMessages,
          model,
          thinking_enabled: thinkingEnabled,
          system_prompt: systemPrompt,
          chat_id: chatIdRef.current,
          enabled_tools: enabledTools,
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        updateAssistantMessage(msg => ({ ...msg, content: `Error: ${response.status} ${errorText}`, isStreaming: false }));
        setIsStreaming(false);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let thinkingStarted = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';

        for (const part of parts) {
          if (!part.trim()) continue;

          const lines = part.split('\n');
          let eventType = '';
          let eventData = '';

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7);
            } else if (line.startsWith('data: ')) {
              eventData = line.slice(6);
            }
          }

          if (!eventType || !eventData) continue;

          let data;
          try {
            data = JSON.parse(eventData);
          } catch {
            continue;
          }

          switch (eventType) {
            case 'message_start':
              // Store chat_id from newly created chats
              if (data.chat_id) {
                setChatIdAndRef(data.chat_id);
                // Notify parent so sidebar can optimistically show the new chat
                if (isNewChat && onChatCreatedRef.current) {
                  onChatCreatedRef.current(data.chat_id, text);
                }
              }
              break;

            case 'thinking':
              if (!thinkingStarted) thinkingStarted = true;
              updateAssistantMessage(msg => ({
                ...msg,
                thinking: msg.thinking + data.text,
              }));
              break;

            case 'content_delta':
              if (thinkingStarted) {
                thinkingStarted = false;
                updateAssistantMessage(msg => ({ ...msg, thinkingDone: true }));
              }
              updateAssistantMessage(msg => ({
                ...msg,
                content: msg.content + data.text,
              }));
              break;

            case 'tool_use_start':
              updateAssistantMessage(msg => ({
                ...msg,
                toolUses: [...msg.toolUses, {
                  id: data.id,
                  name: data.name,
                  input: data.input,
                  output: null,
                  done: false,
                }],
              }));
              break;

            case 'tool_result':
              updateAssistantMessage(msg => ({
                ...msg,
                toolUses: msg.toolUses.map(tu =>
                  tu.id === data.id
                    ? { ...tu, output: data.output, file_ids: data.file_ids || [], done: true, ...(data.input && { input: data.input }) }
                    : tu
                ),
              }));
              break;

            case 'message_done':
              if (thinkingStarted) {
                updateAssistantMessage(msg => ({ ...msg, thinkingDone: true }));
              }
              // Store chat_id from message_done (for newly created chats)
              if (data.chat_id) {
                setChatIdAndRef(data.chat_id);
              }
              updateAssistantMessage(msg => ({
                ...msg,
                isStreaming: false,
                usage: data.usage,
              }));
              break;

            case 'chat_restricted':
              // User is restricted — remove the placeholder assistant message and notify
              setMessagesAndRef(prev => prev.filter(m => m.role !== 'assistant' || m.content));
              if (onChatRestrictedRef.current) {
                onChatRestrictedRef.current(data.reason, data.details);
              }
              break;

            case 'error':
              updateAssistantMessage(msg => ({
                ...msg,
                content: msg.content + `\n\nError: ${data.message}`,
                isStreaming: false,
              }));
              break;

            default:
              break;
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        updateAssistantMessage(msg => ({ ...msg, isStreaming: false }));
      } else {
        updateAssistantMessage(msg => ({
          ...msg,
          content: msg.content + `\n\nError: ${err.message}`,
          isStreaming: false,
        }));
      }
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  }, [isStreaming, updateAssistantMessage, setMessagesAndRef, setChatIdAndRef]);

  const loadChat = useCallback(async (id) => {
    // Don't interrupt an active stream
    if (abortControllerRef.current) return;

    // Optimistically set chatId and clear messages immediately
    setChatIdAndRef(id);
    setMessagesAndRef([]);
    setIsLoadingChat(true);

    try {
      const res = await apiRequest(`/chat/${id}`);
      if (!res.ok) return;
      const data = await res.json();

      // Only apply if this chat is still active (user may have navigated away)
      if (chatIdRef.current !== id) return;

      // Convert backend messages to UI format
      const uiMessages = data.messages.map(m => ({
        id: m.id,
        role: m.role,
        content: m.content || '',
        thinking: m.thinking || '',
        thinkingDone: !!m.thinking,
        toolUses: (m.tool_uses || []).map(tu => ({ ...tu, file_ids: tu.file_ids || [], done: true })),
        isStreaming: false,
        usage: m.usage || undefined,
        attachments: m.attachments || undefined,
      }));

      setMessagesAndRef(uiMessages);
    } catch (err) {
      console.error('Failed to load chat:', err);
    } finally {
      if (chatIdRef.current === id) {
        setIsLoadingChat(false);
      }
    }
  }, [setMessagesAndRef, setChatIdAndRef]);

  const stopStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  const clearChat = useCallback(() => {
    // Don't clear during an active stream
    if (abortControllerRef.current) return;
    setMessagesAndRef([]);
    setChatIdAndRef(null);
  }, [setMessagesAndRef, setChatIdAndRef]);

  return { messages, isStreaming, isLoadingChat, chatId, sendMessage, stopStreaming, clearChat, loadChat };
}
